import React, { useState } from "react";
import { Search, Filter, Calendar, AlertCircle, FileText, ChevronRight, User } from "lucide-react";
import RightDrawer from "../../shared/components/RightDrawer";

const CONTRACT_TYPE_COLORS = {
  Permanent: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-100",
  },
  Contract: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-100",
  },
  Probation: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-100",
  },
};

const getDaysLeftConfig = (days) => {
  if (days <= 15) return { bg: "bg-red-100", text: "text-red-600", label: "Critical" };
  if (days <= 30) return { bg: "bg-amber-100", text: "text-amber-700", label: "Warning" };
  return { bg: "bg-emerald-100", text: "text-emerald-700", label: "Normal" };
};

const getAvatarColor = (initials) => {
  const colors = [
    "bg-blue-500",
    "bg-emerald-500",
    "bg-purple-500",
    "bg-amber-500",
    "bg-rose-500",
  ];
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash += initials.charCodeAt(i);
  }
  return colors[hash % colors.length];
};

// Full upcoming contract expiry dataset for RightDrawer
const ALL_EXPIRIES = [
  {
    id: "exp-1",
    name: "Rahul Sharma",
    contractId: "CT-1001",
    contractType: "Permanent",
    expiryDate: "Aug 15, 2026",
    daysLeft: 11,
    avatar: "RS",
    department: "Engineering",
    manager: "Aarav Sharma",
    startDate: "Aug 15, 2025",
  },
  {
    id: "exp-2",
    name: "Neha Gupta",
    contractId: "CT-1045",
    contractType: "Contract",
    expiryDate: "Aug 22, 2026",
    daysLeft: 18,
    avatar: "NG",
    department: "Marketing",
    manager: "Kavya Reddy",
    startDate: "Feb 22, 2026",
  },
  {
    id: "exp-3",
    name: "Amit Verma",
    contractId: "CT-1058",
    contractType: "Permanent",
    expiryDate: "Sep 05, 2026",
    daysLeft: 32,
    avatar: "AV",
    department: "Engineering",
    manager: "Arjun Verma",
    startDate: "Sep 05, 2023",
  },
  {
    id: "exp-4",
    name: "Riya Malhotra",
    contractId: "CT-1077",
    contractType: "Probation",
    expiryDate: "Sep 18, 2026",
    daysLeft: 45,
    avatar: "RM",
    department: "Operations",
    manager: "Sneha Pillai",
    startDate: "Jun 18, 2026",
  },
  {
    id: "exp-5",
    name: "Sanya Malhotra",
    contractId: "CT-0980",
    contractType: "Contract",
    expiryDate: "Oct 01, 2026",
    daysLeft: 58,
    avatar: "SM",
    department: "Sales",
    manager: "Kiran Patel",
    startDate: "Oct 01, 2025",
  },
  {
    id: "exp-6",
    name: "Deepak Jain",
    contractId: "CT-0911",
    contractType: "Permanent",
    expiryDate: "Nov 15, 2026",
    daysLeft: 103,
    avatar: "DJ",
    department: "Operations",
    manager: "Sneha Pillai",
    startDate: "Nov 15, 2024",
  },
];

const UpcomingContractExpiry = ({ data = [] }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const filters = ["All", "Permanent", "Contract", "Probation", "Critical (<15 Days)"];

  const displayRows = data.length ? data : ALL_EXPIRIES.slice(0, 4);

  const filteredExpiries = ALL_EXPIRIES.filter((item) => {
    let matchesFilter = true;
    if (activeFilter === "Critical (<15 Days)") {
      matchesFilter = item.daysLeft <= 15;
    } else if (activeFilter !== "All") {
      matchesFilter = item.contractType === activeFilter;
    }

    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.contractId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.department.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  return (
    <>
      {/* Widget Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col justify-between h-full min-h-[320px]">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">
            Upcoming Contract Expiry
          </h3>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="text-xs font-medium text-[#147db5] hover:text-[#116d9d] transition-colors cursor-pointer"
          >
            View all
          </button>
        </div>

        {displayRows.length ? (
          <>
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-2 mb-2 px-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Employee
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Contract Type
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Expiry Date
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 text-right">
                Days Left
              </span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-50">
              {displayRows.map((item) => {
                const typeConfig =
                  CONTRACT_TYPE_COLORS[item.contractType] || CONTRACT_TYPE_COLORS.Contract;
                const daysConfig = getDaysLeftConfig(item.daysLeft);
                const avatarColor = getAvatarColor(item.avatar);

                return (
                  <div
                    key={item.id}
                    onClick={() => setDrawerOpen(true)}
                    className="grid grid-cols-4 gap-2 py-3 px-1 hover:bg-slate-50/80 rounded-lg -mx-1 transition-colors cursor-pointer items-center"
                  >
                    {/* Employee */}
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white text-[10px] font-bold ${avatarColor}`}
                      >
                        {item.avatar}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-800 truncate">
                          {item.name}
                        </p>
                        <p className="text-[10px] text-slate-400 truncate">
                          {item.contractId}
                        </p>
                      </div>
                    </div>

                    {/* Contract Type */}
                    <div>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${typeConfig.bg} ${typeConfig.text} ${typeConfig.border}`}
                      >
                        {item.contractType}
                      </span>
                    </div>

                    {/* Expiry Date */}
                    <span className="text-xs text-slate-600">{item.expiryDate}</span>

                    {/* Days Left */}
                    <div className="flex justify-end">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${daysConfig.bg} ${daysConfig.text}`}
                      >
                        {item.daysLeft} days
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex h-36 items-center justify-center">
            <p className="text-sm text-slate-400">No expiring contracts</p>
          </div>
        )}
      </div>

      {/* Right Drawer for View All */}
      <RightDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Upcoming Contract Expiries"
        description="Review all active employment contracts and probation periods scheduled for expiry or renewal."
        width="540px"
        footer={
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">
              Showing {filteredExpiries.length} of {ALL_EXPIRIES.length} contracts
            </span>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        <div className="p-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by employee name, ID, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-9 pr-4 py-2 text-xs font-medium text-slate-700 outline-none focus:border-[#147db5] focus:bg-white transition-all"
            />
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            <Filter size={13} className="text-slate-400 mr-1 shrink-0" />
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setActiveFilter(f)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all shrink-0 ${activeFilter === f
                    ? "bg-[#147db5] text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200/70"
                  }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Expiry Cards List */}
          <div className="space-y-3 pt-2">
            {filteredExpiries.map((item) => {
              const typeConfig =
                CONTRACT_TYPE_COLORS[item.contractType] || CONTRACT_TYPE_COLORS.Contract;
              const daysConfig = getDaysLeftConfig(item.daysLeft);
              const avatarColor = getAvatarColor(item.avatar);

              return (
                <div
                  key={item.id}
                  className="rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    {/* Employee Info */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white text-xs font-bold ${avatarColor}`}
                      >
                        {item.avatar}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {item.name}
                          </p>
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${typeConfig.bg} ${typeConfig.text} ${typeConfig.border}`}
                          >
                            {item.contractType}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          ID: {item.contractId} • {item.department}
                        </p>
                      </div>
                    </div>

                    {/* Days Left Pill */}
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold shrink-0 ${daysConfig.bg} ${daysConfig.text}`}
                    >
                      {item.daysLeft} days left
                    </span>
                  </div>

                  {/* Metadata Row */}
                  <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600">
                    <div>
                      <span className="text-slate-400 block text-[10px]">Start Date</span>
                      <span className="font-medium text-slate-700">{item.startDate}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Expiry Date</span>
                      <span className="font-semibold text-slate-800">{item.expiryDate}</span>
                    </div>
                  </div>

                  {/* Action Row */}
                  <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
                    <span className="text-[11px] text-slate-400">
                      Manager: <strong className="font-medium text-slate-600">{item.manager}</strong>
                    </span>

                    <button
                      type="button"
                      onClick={() => alert(`Initiating contract renewal for ${item.name} (${item.contractId})`)}
                      className="flex items-center gap-1 text-xs font-semibold text-[#147db5] hover:text-[#116d9d] transition-colors"
                    >
                      Renew Contract
                      <ChevronRight size={13} />
                    </button>
                  </div>
                </div>
              );
            })}

            {filteredExpiries.length === 0 && (
              <div className="py-12 text-center">
                <p className="text-xs text-slate-500 font-medium">No matching contract expiries found.</p>
                <p className="text-[11px] text-slate-400 mt-1">Try adjusting your filters or search keyword.</p>
              </div>
            )}
          </div>
        </div>
      </RightDrawer>
    </>
  );
};

export default UpcomingContractExpiry;
